import React, { useState } from "react";
import "./aboutpage.css";
import { Link } from "react-router-dom";
import { BiArrowBack, BiChevronDown, BiChevronUp, BiDownload } from "react-icons/bi";
import {
  BiLogoHtml5,
  BiLogoCss3,
  BiLogoJavascript,
  BiLogoReact,
  BiLogoNodejs,
  BiLogoJava,
  BiLogoPython,
  BiLogoFigma,
  BiLogoGithub,
  BiCodeAlt,
} from "react-icons/bi";
import { SiMui, SiTailwindcss, SiPostman, SiStripe } from "react-icons/si";

const RESUME_PATH = "/Assets/Amaan_Siddiqui_Resume.pdf";

const skillGroups = [
  {
    title: "Languages & Frameworks",
    skills: [
      { icon: <BiLogoJavascript />, label: "JavaScript" },
      { icon: <BiLogoReact />, label: "React.js" },
      { icon: <BiLogoNodejs />, label: "Node.js / Express" },
      { icon: <BiLogoJava />, label: "Java" },
      { icon: <BiLogoPython />, label: "Python" },
      { icon: <BiLogoHtml5 />, label: "HTML5" },
      { icon: <BiLogoCss3 />, label: "CSS3" },
    ],
  },
  {
    title: "UI Libraries & Styling",
    skills: [
      { icon: <SiMui />, label: "Material UI" },
      { icon: <BiCodeAlt />, label: "shadcn/ui" },
      { icon: <SiTailwindcss />, label: "Tailwind CSS" },
    ],
  },
  {
    title: "Integrations & Tools",
    skills: [
      { icon: <SiStripe />, label: "Stripe SDK" },
      { icon: <BiLogoGithub />, label: "Git / GitHub" },
      { icon: <SiPostman />, label: "Postman" },
      { icon: <BiLogoFigma />, label: "Figma" },
    ],
  },
];

const activities = [
  {
    title: "GeeksforGeeks Student Chapter Lead",
    date: "2022 — 2023",
    description:
      "Drove a coding culture on campus by organizing workshops and events; developed management and leadership skills.",
  },
  {
    title: "Core Member, Programmer's Club",
    date: "2023",
    description:
      "Part of the organizing team for \"ERR_404 5.0\", a National Level Hackathon.",
  },
  {
    title: "International Conference Presenter",
    date: "2023",
    description:
      "Presented a project paper at the 3rd International Conference organized by THEEM College of Engineering.",
  },
];

// Full resume content, replicated in the site's own UI theme instead of a raw PDF embed.
const resumeExperience = [
  {
    role: "Software Engineer — Frontend",
    org: "Mosaic-Apps",
    date: "Jun 2024 — Present",
    points: [
      "Develop and maintain responsive, production-grade web applications using React.js, Next.js, and TypeScript.",
      "Integrate REST APIs and third-party SDKs — including Stripe for payments — to deliver end-to-end product features.",
      "Build reusable, accessible UI components using MUI, shadcn/ui, and Tailwind CSS, improving consistency and development speed across the codebase.",
      "Collaborate with designers and backend engineers to translate product requirements into performant, well-structured frontend architecture.",
    ],
  },
  {
    role: "Frontend Developer Intern",
    org: "Cloud Native Compliance Maven (CNCM) LLC",
    date: "May 2023 — May 2024",
    points: [
      "Executed UI/UX designs using React.js and Material UI to optimize user interaction and engagement.",
      "Implemented UX enhancements that streamlined user interactions, improving overall usability and satisfaction.",
      "Enhanced the design and functionality of multiple components, addressing issues and optimizing performance.",
      "Tech used: HTML, CSS, JavaScript, Figma, React.js, Material UI, Postman, GitHub.",
    ],
  },
  {
    role: "Maths & Science Tutor",
    org: "Self-Employed",
    date: "2021 — 2023",
    points: [
      "Delivered personalized tutoring to middle and high school students, strengthening core concepts in Mathematics and Science.",
    ],
  },
];

const resumeEducation = [
  {
    institute: "University of Mumbai",
    detail: "B.E. in Computer Engineering — CGPA: 8.84/10",
    date: "2020 — 2024",
  },
  {
    institute: "Don Bosco Senior Secondary School",
    detail: "Class XII (PCM + Computer Science), CBSE — 78.2%",
    date: "2019",
  },
  {
    institute: "Don Bosco Senior Secondary School",
    detail: "Class X, CBSE — 93.1%",
    date: "2017",
  },
];

function AboutPage() {
  const [showResume, setShowResume] = useState(false);

  return (
    <div className="aboutpage">
      <div className="aboutpage__container container">
        <Link to="/" className="aboutpage__back">
          <BiArrowBack /> Back to Home
        </Link>

        <header className="aboutpage__intro">
          <span className="aboutpage__eyebrow">KNOW MORE</span>
          <h1>
            Hey, I'm <span className="highlight">Amaan</span>.
          </h1>
          <p>
            I'm a frontend-focused Software Engineer with 2+ years of experience
            building responsive, production-grade web applications using
            React.js, Next.js, and TypeScript. I got into web development
            because I liked seeing an idea turn into something people could
            actually click through — that's still what keeps me hooked today.
          </p>
          <p>
            Day to day, I work on component architecture, API integrations,
            and third-party SDKs like Stripe, translating designs into
            interfaces that feel fast and intentional. Outside of shipping
            features, I enjoy exploring new tools in the React ecosystem and
            occasionally tutoring students in Math and Science.
          </p>

          <div className="aboutpage__resume-actions">
            <button
              className="btn aboutpage__view-resume-btn"
              onClick={() => setShowResume((prev) => !prev)}
            >
              {showResume ? "Hide Resume" : "View Resume"}
              {showResume ? <BiChevronUp /> : <BiChevronDown />}
            </button>
            <a
              href={RESUME_PATH}
              download
              className="btn btn-primary aboutpage__resume-btn"
            >
              <BiDownload /> Download Resume
            </a>
          </div>
        </header>

        {showResume && (
          <section className="resume-ui">
            <div className="resume-ui__header">
              <h2>Mohd Amaan Siddiqui</h2>
              <span className="resume-ui__title">
                Software Engineer — Frontend (React.js / Next.js)
              </span>
              <div className="resume-ui__contact">
                <span>Mumbai, India</span>
                <span>siddiquiamaan13@gmail.com</span>
                <a
                  href="https://linkedin.com/in/amaan-siddiqui-4512a2134"
                  target="_blank"
                  rel="noreferrer"
                >
                  linkedin.com/in/amaan-siddiqui-4512a2134
                </a>
              </div>
            </div>

            <div className="resume-ui__block">
              <h3 className="resume-ui__heading">Summary</h3>
              <p className="resume-ui__summary">
                Frontend-focused Software Engineer with 2+ years of experience
                building responsive, production-grade web applications using
                React.js, Next.js, and TypeScript. Skilled at API integration,
                component architecture, and working with modern UI libraries
                and third-party SDKs (Stripe, MUI, shadcn/ui, Tailwind CSS).
                Strong track record of translating designs into performant,
                user-friendly interfaces.
              </p>
            </div>

            <div className="resume-ui__block">
              <h3 className="resume-ui__heading">Technical Skills</h3>
              <div className="resume-ui__skills">
                {skillGroups.map((group) => (
                  <div key={group.title} className="resume-ui__skills-row">
                    <span className="resume-ui__skills-label">{group.title}</span>
                    <span className="resume-ui__skills-value">
                      {group.skills.map((s) => s.label).join(", ")}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="resume-ui__block">
              <h3 className="resume-ui__heading">Professional Experience</h3>
              {resumeExperience.map((exp) => (
                <div className="resume-ui__entry" key={exp.role + exp.org}>
                  <div className="resume-ui__entry-top">
                    <div>
                      <span className="resume-ui__entry-role">{exp.role}</span>
                      <span className="resume-ui__entry-org"> · {exp.org}</span>
                    </div>
                    <span className="resume-ui__entry-date">{exp.date}</span>
                  </div>
                  <ul>
                    {exp.points.map((point, idx) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="resume-ui__block">
              <h3 className="resume-ui__heading">Education</h3>
              {resumeEducation.map((edu, idx) => (
                <div className="resume-ui__entry resume-ui__entry--compact" key={idx}>
                  <div className="resume-ui__entry-top">
                    <div>
                      <span className="resume-ui__entry-role">{edu.institute}</span>
                    </div>
                    <span className="resume-ui__entry-date">{edu.date}</span>
                  </div>
                  <p className="resume-ui__entry-detail">{edu.detail}</p>
                </div>
              ))}
            </div>

            <div className="resume-ui__block">
              <h3 className="resume-ui__heading">Leadership &amp; Additional Activities</h3>
              <ul className="resume-ui__leadership">
                {activities.map((activity) => (
                  <li key={activity.title}>
                    <span className="resume-ui__entry-role">{activity.title}</span>
                    {" — "}
                    {activity.description}
                  </li>
                ))}
              </ul>
            </div>

            <a
              href={RESUME_PATH}
              download
              className="btn btn-primary resume-ui__download-btn"
            >
              <BiDownload /> Download as PDF
            </a>
          </section>
        )}

        <section className="aboutpage__section">
          <h2>Skills</h2>
          <div className="aboutpage__skills-grid">
            {skillGroups.map((group) => (
              <div className="aboutpage__skill-card" key={group.title}>
                <h3>{group.title}</h3>
                <ul>
                  {group.skills.map((skill) => (
                    <li key={skill.label}>
                      <span className="aboutpage__skill-icon">{skill.icon}</span>
                      {skill.label}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="aboutpage__section">
          <h2>Leadership &amp; Activities</h2>
          <div className="aboutpage__activities">
            {activities.map((activity) => (
              <div className="aboutpage__activity-card" key={activity.title}>
                <span className="aboutpage__activity-date">{activity.date}</span>
                <h3>{activity.title}</h3>
                <p>{activity.description}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default AboutPage;